/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import usersActions from '@sportninja/common/actions/users'
import { bindActionToPromise } from '@sportninja/common/actions/utils'
import req from '@sportninja/common/api/request'
import { ROUTES } from '@sportninja/common/constants/app'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { getSelfUser } from '@sportninja/common/selectors/users'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'

import { font } from '../../components/css'
import { selfForm } from '../../components/Form/form-configs'
import FormSheet from '../../components/Form/FormSheet'
import Icon from '../../components/Icon'
import { Flex } from '../../components/Layout'
import Bio from '../../components/PageTitle/Bio'

import { useUsersMeSuspensions } from '@sportninja/common/hooks/useUsersMeSuspensions'
import PlayerSuspensionsList from 'src/components/PlayerSuspensionsList'
import useWindowSize from 'src/hooks/useWindowSize'
import { media, Mobile } from '../../components/Responsive'
import {
  ProfileMenu,
  ProfileName,
  ProfileTop,
  ProfileWrap,
} from './ProfileComponents'
import PlayerStatsBySeason from './v2/PlayerStatsBySeason'
import PlayerProfileMembership from 'src/components/PlayerProfileMembership'
import { Alert, Tooltip } from 'antd'
import NotificationSettingsGearIcon from '../../components/icons/NotificationSettingsGearIcon'
import NotificationSettingsModal from '../../components/NotificationSettingsModal'

const onEditError = (_, error) => {
  if (error.invalid_fields) {
    const errors = {}
    if (error.invalid_fields.height) {
      errors.height = t('errors:heightMustBeIn')
    }
    if (error.invalid_fields.weight) {
      errors.weight = t('errors:weightMustBeIn')
    }
    return errors
  }
}

const ProfileActionButtons = ({ onClick, onSettingsClick }) => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width < 768, [width])
  const iconSize = isMobile ? 24 : 16

  return (
    <div
      css={css`
        position: absolute;
        right: 0;
        display: flex;
        align-items: center;
        gap: 8px;

        ${isMobile &&
        `
          top: 90px;
          left: 28px;
          right: 0;
        `}
      `}
    >
      <Tooltip title='Edit Profile' mouseEnterDelay={1}>
        <button
          type='button'
          onClick={onClick}
          css={css`
            ${font.body}
            cursor: pointer;
            display: flex;
            justify-content: center;
            align-items: center;
            transition: opacity 0.2s ease-in-out;

            &:hover {
              opacity: 0.8;
            }
          `}
        >
          <Icon
            name='pencil'
            fontSize={iconSize}
            color={colors.DEFAULT_FLAIR}
          />
        </button>
      </Tooltip>
      <Tooltip title='Notification Settings' mouseEnterDelay={1}>
        <button
          type='button'
          onClick={onSettingsClick}
          css={css`
            cursor: pointer;
            display: flex;
            justify-content: center;
            align-items: center;
            transition: opacity 0.2s ease-in-out;

            &:hover {
              opacity: 0.8;
            }
          `}
        >
          <NotificationSettingsGearIcon
            size={22}
            color={colors.DEFAULT_FLAIR}
          />
        </button>
      </Tooltip>
    </div>
  )
}

const ProfileActionButtonsComponent = (props) => (
  <ProfileActionButtons {...props} />
)

/**
 * Create a ProfileActionButtons component with bound settings handler
 */
const createProfileActionButtons = (onSettingsClick) => (props) =>
  <ProfileActionButtonsComponent {...props} onSettingsClick={onSettingsClick} />

/**
 * Sorts items by date in descending order (most recent first)
 * @param {Array} items - Array of items to sort
 * @param {string} dateField - The field name containing the date to sort by
 * @returns {Array} - Sorted array of items with most recent ones first
 */
const sortByDate = (items, dateField = 'end_date') => {
  return [...items].sort((a, b) => {
    const dateA = dayjs(a[dateField])
    const dateB = dayjs(b[dateField])
    if (dateA.isAfter(dateB)) {
      return -1
    } else if (dateA.isBefore(dateB)) {
      return 1
    } else {
      return 0
    }
  })
}

const Profile = ({ selfUser, updateUser, isInFrame }) => {
  const [item, setItem] = useState({})
  const [selfPlayerIds, setSelfPlayerIds] = useState([])
  const [players, setPlayers] = useState([])
  const [loadedPlayers, setLoadedPlayers] = useState(false)
  const [showStats, setShowStats] = useState(true)
  const [isNotificationSettingsOpen, setIsNotificationSettingsOpen] =
    useState(false)

  const ProfileActionButtonsWithSettings = useMemo(
    () => createProfileActionButtons(() => setIsNotificationSettingsOpen(true)),
    []
  )

  const readItem = async () => {
    req('/users/me').then((response) => {
      setItem(response.data)
    })
  }

  const {
    data: suspensions,
    loading,
    isSuspended,
    refetch,
  } = useUsersMeSuspensions(false)

  useEffect(() => {
    readItem()
    refetch()

    req('/users/me/players3').then((response) => {
      if (response.data.length > 0) {
        // Extract player_ids and remove duplicates using Set
        setSelfPlayerIds([...new Set(response.data.map((p) => p.id))])
        setPlayers(response.data)
        setLoadedPlayers(true)
        setShowStats(true)
      } else {
        setShowStats(false)
        setLoadedPlayers(true)
      }
    })
  }, [refetch, selfUser?.id])

  const getSuspensionStatusText = (suspension) => {
    if (suspension?.end_date) {
      return `You're Suspended Until ${dayjs(suspension.end_date)
        .utc()
        .format('MMM D, YYYY')} (${suspension.description})`
    } else {
      return `You're Suspended Indefinitely (${suspension.description})`
    }
  }

  const pages = useMemo(
    () =>
      [
        showStats && {
          text: t('Web:statistics'),
          to: ROUTES.PROFILE.ROOT,
          component: (
            <PlayerStatsBySeason
              playerIds={selfPlayerIds}
              setShowStats={setShowStats}
              players={players}
            />
          ),
        },
        // {
        //   text: 'Membership',
        //   to: ROUTES.PROFILE.MEMBERSHIP,
        //   component: <PlayerProfileMembership />,
        // },
        {
          text: 'Suspensions',
          to: ROUTES.PROFILE.SUSPENSIONS,
          component: (
            <PlayerSuspensionsList
              isProfileView
              loading={loading}
              suspensions={sortByDate(suspensions)}
            />
          ),
        },
      ].filter((p) => typeof p !== 'undefined' && typeof p !== 'boolean'),
    [showStats, selfPlayerIds, loading, suspensions]
  )

  const BASE_ROUTE = ROUTES.PROFILE.ROOT

  return (
    <ProfileWrap baseRoute={BASE_ROUTE} pages={pages} loaded={loadedPlayers}>
      <ProfileTop>
        <Flex
          css={css`
            position: relative;
          `}
        >
          <Mobile>
            {(isMobile) => {
              return (
                <>
                  <img
                    src={
                      getImageThumbnailId(item) ||
                      '/images/generic_placeholders/placeholder_player.png'
                    }
                    alt='Player Suspension'
                    css={css`
                      width: 128px;
                      height: 128px;
                      border-radius: 64px;
                      border: 2px solid ${colors.WHITE};
                    `}
                    onError={(e) => {
                      e.target.src =
                        '/images/generic_placeholders/placeholder_player.png'
                    }}
                  />

                  <FormSheet
                    key='edit'
                    form={selfForm(selfUser, true)}
                    label={t('Web:editProfile')}
                    onSubmit={async (...args) => {
                      if (args && args[0]) {
                        if (args[0]?.phone_number) {
                          if (!args[0]?.phone_number_country) {
                            args[0].phone_number_country = 'US'
                          }
                        }
                      }
                      await updateUser(selfUser.id, ...args)
                      await readItem()
                      return true
                    }}
                    title={t('Web:editProfile')}
                    Button={ProfileActionButtonsWithSettings}
                  />
                </>
              )
            }}
          </Mobile>
          <Flex
            column
            justifyContent='center'
            css={css`
              margin-left: 16px;

              ${media.mobile} {
                margin-top: 0px;
              }
            `}
          >
            <ProfileName item={item} />
            <Bio
              birthDate={item.birth_date}
              height={item.height}
              weight={item.weight}
            />

            {isSuspended &&
              suspensions &&
              suspensions
                .filter((s) => s.is_active)
                .map((suspension) => (
                  <Alert
                    key={suspension.id}
                    showIcon
                    message={getSuspensionStatusText(suspension)}
                    type='error'
                    css={css`
                      margin-top: 16px;
                    `}
                  />
                ))}
          </Flex>
        </Flex>
      </ProfileTop>
      <ProfileMenu baseRoute={BASE_ROUTE} pages={pages} isInFrame={isInFrame} />
      <NotificationSettingsModal
        isOpen={isNotificationSettingsOpen}
        onClose={() => setIsNotificationSettingsOpen(false)}
      />
    </ProfileWrap>
  )
}

const mapStateToProps = () => {
  return (state) => {
    return {
      selfUser: getSelfUser(state),
      isInFrame: state.auth.inFrame,
    }
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    updateUser: bindActionToPromise(dispatch, usersActions.update.request),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Profile)
